import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useDialog } from "@platform/store";
import { useFetchSupportArticles, useFetchArticle } from "@core/features/support";
import { useSupportSheet } from "@platform/store";
import { MdxArticleLinkProvider } from "@themes/pollinator/ui/MdxRenderer";
import { SupportArticleSheet } from "./SupportArticleSheet";
import { useArticleNavigation } from "./useArticleNavigation";

export function SupportArticleSheetContainer() {
  const { openDialog } = useDialog();
  const { id: programId } = useParams<{ id: string }>();

  const { data } = useFetchSupportArticles(programId);
  const articles = data?.articles ?? [];
  const categories = data?.categories ?? [];

  const { isOpen, articleId, close, setArticleId } = useSupportSheet();

  // First, try to find article in preloaded support articles
  const supportArticle = useMemo(() => {
    return articles.find((article) => article.id === articleId);
  }, [articleId, articles]);

  // If not found in support articles, fetch it directly by slug
  // This handles informational articles that aren't in the support collection
  const { data: fetchedArticle } = useFetchArticle(
    supportArticle ? null : articleId, // Only fetch if not found in support articles
    isOpen && !supportArticle && !!articleId
  );

  // Use support article if found, otherwise use fetched article
  const article = supportArticle ?? fetchedArticle ?? null;

  const { prev, next } = useArticleNavigation({
    articleId: articleId ?? null,
    programId,
  });

  const handleSearchClick = () => {
    openDialog("supportSearch");
  };

  const handleTicketClick = () => {
    openDialog("supportTicket");
  };

  // Don't render sheet content until article is loaded
  if (!article) return null;

  return (
    <MdxArticleLinkProvider onArticleSelect={setArticleId}>
      <SupportArticleSheet
        article={article}
        categories={categories}
        isOpen={isOpen}
        onClose={close}
        prev={prev}
        next={next}
        setSheetArticleId={setArticleId}
        onSearchClick={handleSearchClick}
        onTicketClick={handleTicketClick}
      />
    </MdxArticleLinkProvider>
  );
}

export default SupportArticleSheetContainer;

